import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription 
} from '../../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

// State for delete backup confirmation will be added in the component
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import api from '../../services/api';
import { toast } from 'sonner';
import { 
  Download, 
  Upload, 
  Database, 
  Clock, 
  CheckCircle,
  FileJson,
  HardDrive,
  History,
  Shield,
  Loader2,
  Calendar,
  Play,
  Trash2,
  Settings
} from 'lucide-react';

const DAYS_OF_WEEK = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' }
];

const BackupRestore = () => {
  const [backupInfo, setBackupInfo] = useState(null);
  const [logs, setLogs] = useState([]);
  const [scheduledBackups, setScheduledBackups] = useState([]);
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Restore dialog state
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  
  // Schedule settings dialog
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [scheduleDay, setScheduleDay] = useState('sun');
  const [scheduleFrequency, setScheduleFrequency] = useState('weekly');
  const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState('1');
  const [scheduleHour, setScheduleHour] = useState('2');
  const [savingSchedule, setSavingSchedule] = useState(false);
  
  // Triggering backup
  const [triggering, setTriggering] = useState(false);
  
  // Delete backup confirmation
  const [deleteBackupDialog, setDeleteBackupDialog] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState(null);

  const fetchBackupInfo = async () => {
    try {
      const [infoRes, logsRes, scheduledRes, scheduleRes] = await Promise.all([
        api.get('/admin/backup/info'),
        api.get('/admin/backup/logs'),
        api.get('/admin/backup/scheduled'),
        api.get('/admin/backup/schedule')
      ]);
      setBackupInfo(infoRes.data);
      setLogs(logsRes.data.logs);
      setScheduledBackups(scheduledRes.data.backups);
      setScheduleInfo(scheduleRes.data);
      
      // Set schedule form values
      if (scheduleRes.data) {
        setScheduleDay(scheduleRes.data.day_of_week || 'sun');
        setScheduleHour(String(scheduleRes.data.hour || 2));
        setScheduleFrequency(scheduleRes.data.frequency || 'weekly');
        setScheduleDayOfMonth(String(scheduleRes.data.day_of_month || 1));
      }
    } catch (error) {
      toast.error('Failed to fetch backup info');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackupInfo();
  }, []);

  const handleDownloadBackup = async () => {
    setDownloading(true);
    try {
      const response = await api.get('/admin/backup', { responseType: 'blob' });
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'backup.json';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully');
      fetchBackupInfo();
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadScheduledBackup = async (backupId, filename) => {
    try {
      const response = await api.get(`/admin/backup/scheduled/${backupId}/download`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded');
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  const openDeleteBackupDialog = (backupId) => {
    setBackupToDelete(backupId);
    setDeleteBackupDialog(true);
  };

  const handleDeleteScheduledBackup = async () => {
    if (!backupToDelete) return;
    
    try {
      await api.delete(`/admin/backup/scheduled/${backupToDelete}`);
      toast.success('Backup deleted');
      fetchBackupInfo();
    } catch (error) {
      toast.error('Failed to delete backup');
    } finally {
      setDeleteBackupDialog(false);
      setBackupToDelete(null);
    }
  };

  const handlePreviewRestore = async () => {
    if (!restoreFile) {
      toast.error('Please select a backup file');
      return;
    }
    setPreviewing(true);
    const formData = new FormData();
    formData.append('file', restoreFile);
    try {
      const response = await api.post('/admin/restore/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setRestorePreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not read that backup file');
    } finally {
      setPreviewing(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) {
      toast.error('Please select a backup file');
      return;
    }
    
    setRestoring(true);
    const formData = new FormData();
    formData.append('file', restoreFile);
    
    try {
      const response = await api.post(`/admin/restore?mode=merge`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setRestoreResult(response.data);
      toast.success(`Restore completed: ${response.data.total_inserted} inserted, ${response.data.total_updated} updated`);
      fetchBackupInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
      setConfirmDialog(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.put(`/admin/backup/schedule?frequency=${scheduleFrequency}&day_of_week=${scheduleDay}&day_of_month=${scheduleDayOfMonth}&hour=${scheduleHour}&minute=0`);
      toast.success('Backup schedule updated');
      setScheduleDialog(false);
      fetchBackupInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleTriggerBackup = async () => {
    setTriggering(true);
    try {
      await api.post('/admin/backup/trigger');
      toast.success('Backup triggered successfully');
      fetchBackupInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger backup');
    } finally {
      setTriggering(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <DashboardLayout title="Backup & Restore">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Backup & Restore">
      <div className="space-y-6" data-testid="backup-restore-page">
        {/* Header */}
        <div className="space-y-3">
          <p className="text-slate-600">Create backups and restore your data</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setScheduleDialog(true)}
              data-testid="schedule-settings-btn"
            >
              <Settings className="h-4 w-4 mr-2" />
              Schedule
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setRestoreDialog(true);
                setRestoreFile(null);
                setRestoreResult(null);
                setRestorePreview(null);
              }}
              data-testid="restore-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Restore
            </Button>
            <Button
              onClick={handleDownloadBackup}
              disabled={downloading}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="backup-btn"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {downloading ? 'Creating...' : 'Download Backup'}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Database className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Records</p>
                  <p className="font-heading text-2xl font-bold">{backupInfo?.total_records?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Next Scheduled</p>
                  <p className="font-medium text-slate-900 text-sm" data-testid="next-scheduled-text">
                    {scheduleInfo?.next_run
                      ? new Date(scheduleInfo.next_run).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          // Render in the academy's business timezone so the
                          // admin sees "Mon 5:00 AM" — not the UTC instant
                          // converted to whatever the browser happens to be in.
                          // Falls back to undefined (browser local) only if
                          // the backend somehow forgot to include it.
                          timeZone: scheduleInfo?.timezone || undefined,
                          timeZoneName: 'short',
                        })
                      : 'Not scheduled'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <HardDrive className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Saved Backups</p>
                  <p className="font-heading text-2xl font-bold">{scheduledBackups.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Schedule</p>
                  <p className="font-medium text-slate-900 text-sm">
                    {scheduleInfo?.frequency === 'daily'
                      ? 'Daily'
                      : scheduleInfo?.frequency === 'monthly'
                        ? `Day ${scheduleInfo?.day_of_month || 1} monthly`
                        : DAYS_OF_WEEK.find(d => d.value === scheduleInfo?.day_of_week)?.label || 'Sunday'}
                    {' '}at {scheduleInfo?.hour || 2}:00
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">
              <HardDrive className="h-4 w-4 mr-2" />
              Collections
            </TabsTrigger>
            <TabsTrigger value="scheduled">
              <Calendar className="h-4 w-4 mr-2" />
              Scheduled Backups ({scheduledBackups.length})
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="h-4 w-4 mr-2" />
              Activity Log
            </TabsTrigger>
          </TabsList>

          {/* Collections Tab */}
          <TabsContent value="overview">
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <HardDrive className="h-5 w-5 text-slate-600" />
                  Data Collections
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {backupInfo?.collections?.map((collection) => (
                    <div
                      key={collection}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <span className="text-sm text-slate-700 truncate">
                        {collection.replace(/_/g, ' ')}
                      </span>
                      <Badge variant="secondary" className="ml-2">
                        {backupInfo.current_counts?.[collection] || 0}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduled Backups Tab */}
          <TabsContent value="scheduled">
            <Card className="border border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-slate-600" />
                  Scheduled Backups
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTriggerBackup}
                  disabled={triggering}
                  data-testid="trigger-backup-btn"
                >
                  {triggering ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  {triggering ? 'Creating...' : 'Run Now'}
                </Button>
              </CardHeader>
              <CardContent>
                {scheduledBackups.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No scheduled backups yet</p>
                    <p className="text-sm mt-1">Backups run automatically every week</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {scheduledBackups.map((backup) => (
                      <div
                        key={backup.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-emerald-100 rounded-full">
                            <FileJson className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{backup.filename}</p>
                            <p className="text-sm text-slate-500">
                              {backup.total_records?.toLocaleString()} records • {formatFileSize(backup.file_size)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-slate-600">{formatDate(backup.created_at)}</p>
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {backup.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadScheduledBackup(backup.id, backup.filename)}
                              data-testid={`download-${backup.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteBackupDialog(backup.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-${backup.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="history">
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <History className="h-5 w-5 text-slate-600" />
                  Backup & Restore History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <History className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No backup or restore activity yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {logs.map((log, index) => (
                      <div
                        key={log.id || index}
                        className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg"
                      >
                        <div className={`p-2 rounded-full ${
                          log.action.includes('backup') 
                            ? 'bg-emerald-100' 
                            : 'bg-amber-100'
                        }`}>
                          {log.action.includes('backup') ? (
                            <Download className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <Upload className="h-4 w-4 text-amber-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">
                            {log.action === 'backup_created' && 'Manual Backup Created'}
                            {log.action === 'scheduled_backup_created' && 'Scheduled Backup Created'}
                            {log.action === 'scheduled_backup_failed' && 'Scheduled Backup Failed'}
                            {log.action === 'restore_completed' && 'Restore Completed'}
                          </p>
                          <p className="text-sm text-slate-500">
                            {log.total_records?.toLocaleString() || log.total_inserted || 0} records
                            {log.mode && ` (${log.mode} mode)`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-600">{formatDate(log.created_at)}</p>
                          <p className="text-xs text-slate-400">
                            {log.created_by_name || log.restored_by_name || 'System'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Schedule Settings Dialog */}
        <Dialog open={scheduleDialog} onOpenChange={setScheduleDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Backup Schedule Settings
              </DialogTitle>
              <DialogDescription>
                Configure when automatic backups should run. Backups are retained for 8 weeks.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select value={scheduleFrequency} onValueChange={setScheduleFrequency}>
                  <SelectTrigger data-testid="schedule-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {scheduleFrequency === 'weekly' && (
                <div className="space-y-2">
                  <Label>Day of Week</Label>
                  <Select value={scheduleDay} onValueChange={setScheduleDay}>
                    <SelectTrigger data-testid="schedule-day-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((day) => (
                        <SelectItem key={day.value} value={day.value}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {scheduleFrequency === 'monthly' && (
                <div className="space-y-2">
                  <Label>Day of Month</Label>
                  <Select value={scheduleDayOfMonth} onValueChange={setScheduleDayOfMonth}>
                    <SelectTrigger data-testid="schedule-day-of-month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>
                          {i + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Short months run on their last day instead.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Time (Hour) <span className="text-xs text-slate-500">({scheduleInfo?.timezone || 'America/New_York'})</span></Label>
                <Select value={scheduleHour} onValueChange={setScheduleHour}>
                  <SelectTrigger data-testid="schedule-hour-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700" data-testid="schedule-summary-block">
                <Clock className="h-4 w-4 inline mr-1" />
                Backups will run{' '}
                <strong>
                  {scheduleFrequency === 'daily'
                    ? 'every day'
                    : scheduleFrequency === 'monthly'
                      ? `on day ${scheduleDayOfMonth} of each month`
                      : `every ${DAYS_OF_WEEK.find(d => d.value === scheduleDay)?.label}`}
                </strong>{' '}
                at{' '}
                <strong>{parseInt(scheduleHour) === 0 ? '12:00 AM' : parseInt(scheduleHour) < 12 ? `${scheduleHour}:00 AM` : parseInt(scheduleHour) === 12 ? '12:00 PM' : `${parseInt(scheduleHour) - 12}:00 PM`}</strong>{' '}
                <span className="text-blue-600">({scheduleInfo?.timezone || 'America/New_York'})</span>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setScheduleDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveSchedule}
                disabled={savingSchedule}
                data-testid="save-schedule-btn"
              >
                {savingSchedule ? 'Saving...' : 'Save Schedule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restore Dialog */}
        <Dialog open={restoreDialog} onOpenChange={setRestoreDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-amber-600" />
                Restore from Backup
              </DialogTitle>
              <DialogDescription>
                Upload a backup file to restore your data. Choose how to handle existing data.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Backup File</Label>
                <Input
                  type="file"
                  accept=".json"
                  onChange={(e) => { setRestoreFile(e.target.files[0]); setRestorePreview(null); }}
                  data-testid="restore-file-input"
                />
                {restoreFile && (
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <FileJson className="h-4 w-4" />
                    {restoreFile.name} ({(restoreFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <Label>Restore Mode</Label>
                <div
                  className="flex items-start space-x-3 p-3 border border-border rounded-lg bg-muted/40"
                  data-testid="restore-mode-merge-only"
                >
                  <div>
                    <p className="font-medium text-foreground">Merge (Safe)</p>
                    <p className="text-sm text-muted-foreground">
                      Adds new records and updates existing ones. Existing data is preserved, and the
                      restore only ever touches your own academy&apos;s data.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      The old &ldquo;Replace&rdquo; mode was removed &mdash; it deleted live records
                      before restoring, so one bad file could wipe your academy.
                    </p>
                  </div>
                </div>
              </div>

              {restorePreview && (
                <div
                  className="border border-border rounded-lg p-4 bg-muted/30 space-y-3"
                  data-testid="restore-preview-block"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-foreground flex items-center gap-2">
                      <Shield className="h-4 w-4 text-blue-600" />
                      What this file would change
                    </h4>
                    <span className="text-xs text-muted-foreground">Nothing written yet</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="text-center p-2 rounded bg-emerald-500/10" data-testid="preview-add-count">
                      <p className="font-bold text-emerald-600">{restorePreview.totals.add}</p>
                      <p className="text-xs text-muted-foreground">New</p>
                    </div>
                    <div className="text-center p-2 rounded bg-blue-500/10" data-testid="preview-update-count">
                      <p className="font-bold text-blue-600">{restorePreview.totals.update}</p>
                      <p className="text-xs text-muted-foreground">Updated</p>
                    </div>
                    <div className="text-center p-2 rounded bg-muted">
                      <p className="font-bold text-foreground">{restorePreview.totals.unchanged}</p>
                      <p className="text-xs text-muted-foreground">Unchanged</p>
                    </div>
                    <div className="text-center p-2 rounded bg-muted">
                      <p className="font-bold text-foreground">{restorePreview.totals.skipped}</p>
                      <p className="text-xs text-muted-foreground">Skipped</p>
                    </div>
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-border text-sm">
                    {restorePreview.collections
                      .filter((c) => c.add > 0 || c.update > 0 || c.skipped > 0)
                      .map((c) => (
                        <div key={c.name} className="py-2">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-foreground">{c.name.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-muted-foreground">
                              +{c.add} new · {c.update} updated
                            </span>
                          </div>
                          {c.examples.length > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {c.examples.map((ex, i) => (
                                <li key={i} className="text-xs text-muted-foreground">
                                  {ex.action === 'add' ? 'Add' : 'Update'} &ldquo;{ex.label}&rdquo;
                                  {ex.changed_fields?.length > 0
                                    ? ` — ${ex.changed_fields.join(', ')}`
                                    : ''}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    {restorePreview.totals.add === 0 &&
                      restorePreview.totals.update === 0 && (
                        <p className="py-2 text-muted-foreground">
                          This file matches your current data — a restore would change nothing.
                        </p>
                      )}
                  </div>
                  {restorePreview.truncated && (
                    <p className="text-xs text-amber-600">
                      Large file — preview covers the first {restorePreview.scanned.toLocaleString()} records.
                    </p>
                  )}
                </div>
              )}

              {restoreResult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="font-medium text-emerald-800 flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4" />
                    Restore Complete
                  </h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-emerald-100 rounded">
                      <p className="font-bold text-emerald-700">{restoreResult.total_inserted}</p>
                      <p className="text-emerald-600 text-xs">Inserted</p>
                    </div>
                    <div className="text-center p-2 bg-blue-100 rounded">
                      <p className="font-bold text-blue-700">{restoreResult.total_updated}</p>
                      <p className="text-blue-600 text-xs">Updated</p>
                    </div>
                    <div className="text-center p-2 bg-slate-100 rounded">
                      <p className="font-bold text-slate-700">{restoreResult.total_skipped}</p>
                      <p className="text-slate-600 text-xs">Skipped</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setRestoreDialog(false)}>
                Cancel
              </Button>
              {!restorePreview ? (
                <Button
                  onClick={handlePreviewRestore}
                  disabled={!restoreFile || previewing}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="preview-restore-btn"
                >
                  {previewing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Checking file...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Preview Changes
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={() => setConfirmDialog(true)}
                  disabled={!restoreFile || restoring}
                  className="bg-amber-500 hover:bg-amber-600"
                  data-testid="start-restore-btn"
                >
                  {restoring ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Restore Data
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Backup Confirmation Dialog */}
        <AlertDialog open={deleteBackupDialog} onOpenChange={setDeleteBackupDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                Delete Backup
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this backup? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteScheduledBackup}
                className="bg-red-500 hover:bg-red-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirmation Dialog */}
        <AlertDialog open={confirmDialog} onOpenChange={setConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-amber-600" />
                Confirm Restore
              </AlertDialogTitle>
              <AlertDialogDescription>
                <span>
                  This will merge the backup data with your existing data. New records will be added and existing records will be updated.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={restoring}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleRestore}
                disabled={restoring}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {restoring ? 'Restoring...' : 'Yes, Restore'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default BackupRestore;
